import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { MaleIcon } from 'lib/components/IconsChakra/MaleIcon';
import { RestrictionIcon } from 'lib/components/IconsChakra/RestrictionIcon';
import { hasDestination } from '../OffshoreActivity.utils';
import { getPowerValue } from 'routes/Operations/models/helpers/turbines-model';

const COLORS = {
    defaultBackground: '#8a8d91',
    grey: '#B9B9B9',
    green: '#45D36D',
    red: '#C92A1B',
    orange: '#F2961C',
    black: '#111111',
    white: '#D9D9D9',
    border: '#ffffff',
    restriction: '#8FD2FF',
    inconsistent: '#6E5E1C',
    exception: '#E36B63',
};

function getComputedTileState(data, parkName, parkVessels) {
    const manifested = hasDestination(parkName, parkVessels, data.name);
    const showRestriction = !!data.restrictions?.value;
    const showOccupied = (data.person_count?.value ?? 0) > 0;
    const showTransfer = (data.is_released?.value ?? 0) > 0;
    const pacmanUnavailable = !!data.is_marked_unavailable?.value;
    const inconsistent = (data.inconsistency_count?.value ?? 0) > 0;
    const showException = data.note_type?.value === 2;
    const powerValue = getPowerValue(data);
    const isAvailable = data.state?.value === 'AVAILABLE' || (powerValue ?? 0) > 0;

    let mainBg = COLORS.defaultBackground;
    const textColor = '#F5F5F5';

    if (data.state?.value === 'ALARM_STOPPED') {
        mainBg = COLORS.red;
    } else if (data.state?.value === 'ALARM_RUNNING') {
        mainBg = COLORS.orange;
    } else if (pacmanUnavailable) {
        mainBg = COLORS.black;
    }

    return {
        mainBg,
        textColor,
        leftStripBg: isAvailable ? COLORS.green : 'transparent',
        rightStripBg: showException ? COLORS.exception : COLORS.defaultBackground,
        showBottomStripe: inconsistent,
        bottomStripeBg: inconsistent ? COLORS.inconsistent : undefined,
        showTransfer,
        showRestriction,
        showOccupied,
        manifested,
    };
}

export const OffshoreTurbineBox = ({ turbineData, parkName, parkVessels }) => {
    const { name, too_many_people_warning } = turbineData;
    const computed = getComputedTileState(turbineData, parkName, parkVessels);
    return (
        <Flex
            role="button"
            position="relative"
            minWidth="120px"
            h="40px"
            minH="40px"
            align="stretch"
            overflow="hidden"
            borderColor={COLORS.border}
            bg={computed.mainBg}
            boxSizing="border-box">
            {/* Left green availability strip */}
            <Box w="10px" minW="10px" bg={computed.leftStripBg} flexShrink={0} />

            {/* Main content */}
            <Flex
                flex="1"
                minW={0}
                align="center"
                px="4px"
                gap="12px"
                position="relative"
                bg={
                    computed.showBottomStripe
                        ? `linear-gradient(120deg, ${computed.mainBg} 0%, ${computed.mainBg} 48%, ${computed.bottomStripeBg} 48%, ${computed.bottomStripeBg} 100%)`
                        : computed.mainBg
                }>
                <Text
                    color={computed.textColor}
                    fontWeight="800"
                    fontSize="13px"
                    lineHeight="1"
                    letterSpacing="0.2px"
                    whiteSpace="nowrap">
                    {name}
                </Text>

                {computed.showTransfer && (
                    <Text
                        color={computed.textColor}
                        fontWeight="800"
                        fontSize="14px"
                        lineHeight="1"
                        whiteSpace="nowrap"
                        data-testid="released-r">
                        T
                    </Text>
                )}

                {computed.showRestriction && (
                    <Box display="flex" alignItems="center" justifyContent="center" lineHeight={0}>
                        <RestrictionIcon />
                    </Box>
                )}

                {computed.showOccupied && (
                    <Box display="flex" alignItems="center" justifyContent="center" lineHeight={0}>
                        <MaleIcon color={too_many_people_warning?.value ? 'red' : 'white'} />
                    </Box>
                )}
            </Flex>

            {/* Right integrated chamber */}
            <Box
                w="12px"
                minW="12px"
                bg={computed.rightStripBg}
                border="2px solid"
                borderColor={COLORS.border}
                flexShrink={0}
            />
        </Flex>
    );
};
